import { IconClusterLayer } from './IconClusterLayer'
import { LabeledIconLayer } from 'ifield-core-components'
import SpeechBalloonLabelLayer, {
  DEPTH_GROUP,
} from './SpeechBalloonLabelLayer.js'

/**
 * 種類に対応するdefaultPropsを取得する
 * @param {Object} layer レイヤークラス
 * @param {Object} propsMapping propsのマッピング
 * @returns {Object} defaultProps
 */
const getDefaultProps = (layer, propsMapping) => {
  const result = {}

  if (layer) {
    for (const key in propsMapping) {
      result[key] = layer.defaultProps[propsMapping[key]]
    }
  }

  return result
}

/**
 * レンダリング用props転送
 * @param {Object} layer レイヤークラス
 * @param {Object} propsMapping propsのマッピング
 * @returns {Object} props
 */
const forwardProps = (layer, propsMapping) => {
  const { transitions, updateTriggers } = layer.props
  const result = {
    updateTriggers: {},
    transitions: transitions && {
      getPosition: transitions.getPosition,
    },
  }

  for (const [sourceKey, targetKey] of Object.entries(propsMapping)) {
    let value = layer.props[sourceKey]
    if (sourceKey.startsWith('get')) {
      // v9対応: getSubLayerAccessor は削除されたため、値をそのまま利用する
      result.updateTriggers[targetKey] = updateTriggers[sourceKey]
      if (transitions) {
        result.transitions[targetKey] = transitions[sourceKey]
      }
    }
    result[targetKey] = value
  }

  return result
}

/**
 * 引数がオブジェクトか判定
 * @param {*} value 対象値
 * @returns {Boolean} valueがObjectならtrue
 */
const isObject = (value) => {
  return value !== null && typeof value === 'object'
}

// iconClusterLayerのpropsマッピング
const iconClusterPropsMapping = {
  alphaCutoff: 'alphaCutoff',
  billboard: 'billboard',
  characterSet: 'characterSet',
  fontFamily: 'fontFamily',
  fontSettings: 'fontSettings',
  fontWeight: 'fontWeight',
  getIcon: 'getIcon',
  getIconAngle: 'getIconAngle',
  getIconColor: 'getIconColor',
  getIconPixelOffset: 'getIconPixelOffset',
  getIconSize: 'getIconSize',
  getPosition: 'getPosition',
  getText: 'getText',
  getTextAlignmentBaseline: 'getTextAlignmentBaseline',
  getTextAnchor: 'getTextAnchor',
  getTextAngle: 'getTextAngle',
  getTextBackgroundColor: 'getTextBackgroundColor',
  getTextBorderColor: 'getTextBorderColor',
  getTextBorderWidth: 'getTextBorderWidth',
  getTextColor: 'getTextColor',
  getTextPixelOffset: 'getTextPixelOffset',
  iconAtlas: 'iconAtlas',
  iconMapping: 'iconMapping',
  lineHeight: 'lineHeight',
  onIconError: 'onIconError',
  sizeScale: 'sizeScale',
  sizeUnits: 'sizeUnits',
  sizeMinPixels: 'sizeMinPixels',
  sizeMaxPixels: 'sizeMaxPixels',
  textBackground: 'textBackground',
  textBackgroundPadding: 'textBackgroundPadding',
  textMaxWidth: 'textMaxWidth',
  textOutlineWidth: 'textOutlineWidth',
  textOutlineColor: 'textOutlineColor',
  wordBreak: 'wordBreak',
  maxZoom: 'maxZoom',
  minPoints: 'minPoints',
  minZoom: 'minZoom',
  nodeSize: 'nodeSize',
  onClustered: 'onClustered',
  radius: 'radius',
  tileExtent: 'tileExtent',
  zoom: 'zoom',
}

// 吹き出しラベルのpropsマッピング
const labelPropsMapping = {
  getPosition: 'getPosition',
  getLabel: 'getText',
  getLabelColor: 'getColor',
  getLabelSize: 'getSize',
  getLabelBackgroundColor: 'getBackgroundColor',
  getLabelIcon: 'getIcon',
  getLabelIconColor: 'getIconColor',
  labelIconAtlas: 'iconAtlas',
  labelIconMapping: 'iconMapping',
  labelIconSize: 'iconSize',
  labelIconGap: 'iconGap',
  labelMaxFontSize: 'maxFontSize',
  getLabelDotColor: 'getDotColor',
  getLabelDotBorderColor: 'getDotBorderColor',
  labelPixelOffset: 'basePixelOffset',
  labelCharacterSet: 'characterSet',
  labelTail: 'tail',
  labelShadow: 'shadow',
}

const defaultProps = {
  ...getDefaultProps(SpeechBalloonLabelLayer, labelPropsMapping),
  ...getDefaultProps(IconClusterLayer, iconClusterPropsMapping),
  // visible として `{ geometry: boolean; label: boolean; }` を期待するので、object 型に override する
  visible: { type: 'object', value: true },
}

class TippedIconClusterLayer extends IconClusterLayer {
  renderLayers() {
    const {
      getText,
      getTextSize,
      lineHeight,
      parameters = {},
      visible,
    } = this.props
    const iconClusterProps = forwardProps(this, iconClusterPropsMapping)
    const labelProps = forwardProps(this, labelPropsMapping)

    return [
      new LabeledIconLayer(
        iconClusterProps,
        this.getSubLayerProps({
          id: 'icon',
          data: this.state.data,
          visible: isObject(visible) ? visible.geometry : visible,
          getText,
          lineHeight,
          getTextSize: (d) => {
            if (!d.properties.cluster) return
            // v9対応: getTextSize が関数か定数かを確認して呼び出す
            return typeof getTextSize === 'function'
              ? getTextSize(d)
              : getTextSize
          },
          getPosition: (d) => d.geometry.coordinates,
          updateTriggers: iconClusterProps.updateTriggers,
          // LabeledIconLayer内部のIconLayerへparametersを渡すための設定
          _subLayerProps: {
            icon: {
              ...parameters,
            },
            label: {
              ...parameters,
            },
          },
        })
      ),
      // ラベルは文字単位で描く。文字列が変わっても文字の種類が同じなら作り直しが起きない
      new SpeechBalloonLabelLayer(
        labelProps,
        this.getSubLayerProps({
          id: 'label',
          // まとめられた点にはラベルを出さない
          data: this.state.data.filter((d) => !d.properties.cluster),
          visible: isObject(visible) ? visible.label : visible,
          getPosition: (d) => d.geometry.coordinates,
          // プレイスのラベルと重なったときはユーザーのラベルを手前に出す
          depthBaseRank: DEPTH_GROUP.user,
          updateTriggers: labelProps.updateTriggers,
        }),
        {
          pickable: false,
        }
      ),
    ]
  }
}

TippedIconClusterLayer.layerName = 'TippedIconClusterLayer'
TippedIconClusterLayer.defaultProps = defaultProps

export { TippedIconClusterLayer }
